import React from 'react';
import PageWrapper from '../Layout/PageWrapper';

const SkeletonField = ({ width = "w-24" }) => (
  <div className="space-y-2">
    <div className={`h-4 bg-gray-200 rounded ${width} animate-pulse`}></div>
    <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
  </div>
);

const SkeletonDataProcessing = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 animate-pulse">
    {/* Section Title */}
    <div>
      <div className="h-6 bg-gray-300 rounded w-40 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-80"></div>
    </div>

    {/* Filter Fields */}
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SkeletonField width="w-20" />
        <SkeletonField width="w-24" />
        <SkeletonField width="w-28" />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <div className="h-9 bg-gray-800 rounded w-24 animate-pulse"></div>
        <div className="h-9 bg-gray-200 rounded w-32 animate-pulse"></div>
      </div>

      {/* Error/Info Message */}
      <div className="space-y-2">
        <div className="h-4 bg-red-100 rounded w-48 animate-pulse"></div>
        <div className="h-4 bg-gray-100 rounded w-96 animate-pulse"></div>
      </div>
    </div>

    {/* Fill N/A Section */}
    <div className="space-y-3">
      <div className="h-5 bg-gray-200 rounded w-16 animate-pulse"></div>
      <div className="h-12 bg-gray-200 rounded w-20 animate-pulse"></div>
    </div>
  </div>
);

const SkeletonStatusPanel = ({ isNewJob = false }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4 animate-pulse">
    <div className="h-6 bg-gray-300 rounded w-20"></div>

    {isNewJob ? (
      // New job status panel
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    ) : (
      // Job detail status panel
      <>
        <div className="space-y-3">
          <div className="flex justify-between">
            <div className="h-4 bg-gray-200 rounded w-12"></div>
            <div className="h-6 bg-yellow-100 rounded-full w-20"></div>
          </div>
          <div className="flex justify-between">
            <div className="h-4 bg-gray-200 rounded w-16"></div>
            <div className="h-4 bg-gray-200 rounded w-8"></div>
          </div>
          <div className="flex justify-between">
            <div className="h-4 bg-gray-200 rounded w-14"></div>
            <div className="h-4 bg-gray-200 rounded w-24"></div>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-2">
          <div className="flex justify-between">
            <div className="h-3 bg-gray-200 rounded w-16"></div>
            <div className="h-3 bg-gray-200 rounded w-20"></div>
          </div>
          <div className="flex justify-between">
            <div className="h-3 bg-gray-200 rounded w-16"></div>
            <div className="h-3 bg-gray-200 rounded w-20"></div>
          </div>
        </div>
      </>
    )}

    <div className="border-t border-gray-100 pt-4">
      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
    </div>
  </div>
);

const SkeletonJobForm = ({ isNewJob = false, showEditButtons = false }) => {
  return (
    <PageWrapper>
      {/* Status Panel - Top on small screens */}
      <div className="lg:hidden mb-6">
        <SkeletonStatusPanel isNewJob={isNewJob} />
      </div>

      {/* Main Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content Area */}
        <div className="flex-1">
          {/* Page Header */}
          <div className="mb-6 flex justify-between items-center animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-48"></div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              {isNewJob ? (
                // New job doesn't have edit buttons in header
                null
              ) : showEditButtons ? (
                // Detail page with edit buttons
                <>
                  <div className="h-9 bg-gray-500 rounded w-20"></div>
                  <div className="h-9 bg-green-600 rounded w-16"></div>
                </>
              ) : (
                // Detail page with edit button
                <div className="h-9 bg-blue-600 rounded w-16"></div>
              )}
            </div>
          </div>

          {/* Form Content - Only Data Processing Section */}
          <div className="space-y-6">
            <SkeletonDataProcessing />

            {/* Submit Button (only for new job) */}
            {isNewJob && (
              <div className="flex justify-center pt-4">
                <div className="h-12 bg-gray-800 rounded-lg w-32 animate-pulse"></div>
              </div>
            )}
          </div>
        </div>

        {/* Status Panel - Right side on large screens */}
        <div className="hidden lg:block lg:w-80 lg:flex-shrink-0">
          <div className="sticky top-6">
            <SkeletonStatusPanel isNewJob={isNewJob} />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default SkeletonJobForm;
